import { useState, type FormEvent } from "react";
import { Link, Navigate } from "react-router";
import { Cta } from "../components/Cta";
import { GoogleButton } from "../features/auth/GoogleButton";
import { Section } from "../components/Section";
import { AGE_GATE } from "../content/compliance";
import { TERMS_VERSION } from "../content/legal";
import { useAuth } from "../features/auth/context";
import { ConsentNotice } from "../features/legal/ConsentNotice";
import { OffshoreRefusal, UnderAgeRefusal } from "../features/legal/Refusals";
import { isApiError } from "../services/api/errors";
import { signUp } from "../services/api/signup";
import { usePageMeta } from "../lib/usePageMeta";
import styles from "./Login.module.css";

/**
 * /signup — where a KEV account is actually made.
 *
 * ## Why this page exists rather than a hand-off to Cognito
 *
 * It used to be a disclaimer and a button that sent you to Managed Login's own
 * sign-up form. That form is Cognito's, not ours: it cannot take a date of
 * birth, cannot validate a country, and cannot say why it is asking. Worse, its
 * sign-in page carries an unremovable "New user? Create an account" link, so
 * accounts could be made entirely outside our flow — never having seen the
 * terms.
 *
 * `selfSignUpEnabled: false` closed that door, which left no way to register at
 * all. So registration is here now, and the three things an account requires —
 * a name, an age, a country of residence — are conditions of getting one rather
 * than questions asked afterwards.
 *
 * ## THERE IS NO PASSWORD FIELD, AND THERE MUST NEVER BE ONE
 *
 * kev-security-model.md is explicit that password handling never touches our
 * code. That did not change when the form moved onto our own site: the server
 * calls AdminCreateUser, Cognito generates a temporary password and emails it,
 * and the user sets a real one on Managed Login at first sign-in. The app
 * client still disables userPassword, userSrp, custom and adminUserPassword, so
 * a password collected here could not be used even if someone added the input.
 * Signup.test.tsx asserts no password input renders.
 *
 * ## The success state is not a redirect
 *
 * There is no session yet — the account exists but nobody has signed into it.
 * So the page tells them to check their email, and says the same thing whether
 * or not an account was actually created: a duplicate address returns the same
 * response as a new one, because distinguishing them is an enumeration oracle.
 */
type Ui =
  | { kind: "idle" }
  | { kind: "sending" }
  | { kind: "sent" }
  | { kind: "error"; message: string }
  | { kind: "under-age" }
  | { kind: "offshore" };

export function Signup() {
  usePageMeta("signup");
  const { status } = useAuth();

  const [email, setEmail] = useState("");
  const [givenName, setGivenName] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [birthdate, setBirthdate] = useState("");
  /* LOCKED TO AUSTRALIA (Ned's site cut list, 0.5, 2026-10-05). The field
     stays because the account record stores a country of residence and the
     API still expects one; it is one fixed value now rather than a dropdown
     of countries KEV does not serve. content/countries.ts still feeds the
     Google onboarding form at /welcome. */
  const country = "AU";
  const [ui, setUi] = useState<Ui>({ kind: "idle" });

  /* Already signed in — there is nothing to create. Sending them to /app rather
     than rendering a form they cannot use. */
  if (status === "authenticated") return <Navigate to="/app" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setUi({ kind: "sending" });
    try {
      await signUp({
        email: email.trim(),
        givenName: givenName.trim(),
        familyName: familyName.trim(),
        birthdate,
        country,
        termsVersion: TERMS_VERSION,
      });
    } catch (err) {
      if (isApiError(err) && err.code === "under-age") return setUi({ kind: "under-age" });
      if (isApiError(err) && err.code === "country-unsupported") return setUi({ kind: "offshore" });
      /* The real message, not a house one — the generic version cost two
         debugging rounds on the old age gate, where a client-side parse failure
         and a genuine server error read identically. */
      setUi({
        kind: "error",
        message: err instanceof Error ? err.message : "Something went wrong.",
      });
      return;
    }
    setUi({ kind: "sent" });
  }

  if (ui.kind === "under-age") return <UnderAgeRefusal />;
  if (ui.kind === "offshore") return <OffshoreRefusal />;

  if (ui.kind === "sent") {
    return (
      <Section eyebrow="Account" heading="Check your email" tone="ink" level={1}>
        <div className={styles.wrap}>
          <p className={styles.hint}>
            If that address can hold a KEV account, we&rsquo;ve sent it a temporary
            password. Use it to sign in and you&rsquo;ll be asked to pick your own.
          </p>
          {/* "IF that address can" is doing real work. A duplicate email returns
              the same response as a new one — anything else lets a stranger test
              whether you hold an account here. The sentence has to be true in
              both cases. */}
          <p className={styles.hint}>
            Nothing arrived after a few minutes? Check your spam folder,
            then try signing in. You may already have an account.
          </p>
          <div className={styles.action}>
            <Cta to="/login">Go to sign in</Cta>
          </div>
        </div>
      </Section>
    );
  }

  const busy = ui.kind === "sending";

  return (
    <Section eyebrow="Account" heading="Create account" tone="ink" level={1}>
      <div className={styles.wrap}>
        <form className={styles.form} onSubmit={onSubmit}>
          {/* ABOVE the form, because it is the shorter road: no temporary
              password, no email round-trip. Renders nothing unless this
              environment has Google configured.

              A Google user still gives us a date of birth, a country and a T&C
              acceptance — RequireOnboarded sends them to /welcome, which asks
              for exactly what this form asks for. The 18+ gate is not skipped,
              it moves one screen later. */}
          <GoogleButton next="/app" label="Sign up with Google" />

          <p className={styles.hint}>
            We&rsquo;ll email you a temporary password. Check spam if it doesn&rsquo;t arrive.
          </p>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="email">
              Email
            </label>
            <input
              id="email"
              className={styles.input}
              type="email"
              autoComplete="email"
              required
              maxLength={254}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="givenName">
              First name
            </label>
            <input
              id="givenName"
              className={styles.input}
              type="text"
              autoComplete="given-name"
              required
              maxLength={64}
              value={givenName}
              onChange={(e) => setGivenName(e.target.value)}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="familyName">
              Last name
            </label>
            <input
              id="familyName"
              className={styles.input}
              type="text"
              autoComplete="family-name"
              required
              maxLength={64}
              value={familyName}
              onChange={(e) => setFamilyName(e.target.value)}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="birthdate">
              Date of birth
            </label>
            <input
              id="birthdate"
              className={styles.input}
              type="date"
              autoComplete="bday"
              required
              value={birthdate}
              onChange={(e) => setBirthdate(e.target.value)}
            />
            {/* The server does the arithmetic. A client that computes its own
                eligibility is a client that can claim it. */}
            <p className={styles.note}>{AGE_GATE} only, fantasy included.</p>
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="country">
              Country of residence
            </label>
            <input
              id="country"
              className={styles.input}
              type="text"
              readOnly
              value="Australia"
            />
            <p className={styles.note}>KEV is available in Australia only.</p>
          </div>

          {ui.kind === "error" && (
            <p className={styles.error} role="alert">
              Couldn&rsquo;t create that account just now. Have another
              go.
              <br />
              <span className={styles.errorDetail}>{ui.message}</span>
            </p>
          )}

          {/* ABOVE the button, not below it (Ned, 2026-08-08).
              What someone is agreeing to has to be on screen BEFORE the control
              that agrees to it. Underneath, it reads as small print after the
              decision — and on the phone screenshot that started this, the
              acknowledgement sat below the fold while CREATE ACCOUNT was the
              last thing visible. "By continuing you acknowledge" is only true if
              they could see it before continuing. */}
          <ConsentNotice />

          <Cta type="submit" disabled={busy}>
            {busy ? "One sec…" : "Create account"}
          </Cta>
        </form>

        {/* Paired with sign-in as an equal sibling (Ned, 2026-08-07), not the
            small "already have an account?" line this used to carry. Both doors
            are visible from both pages. */}
        <p className={styles.switchMode}>
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </Section>
  );
}
